class ComplimentaryEmbroidery extends HTMLElement {
  constructor() {
    super();
    this.config = JSON.parse(this.querySelector('[data-embroidery-config]').textContent);
    this.dialog = this.querySelector('.embroidery-dialog');
    this.dialogTitle = this.querySelector('[data-embroidery-title]');
    this.dialogDescription = this.querySelector('[data-embroidery-description]');
    this.upsellStep = this.querySelector('[data-embroidery-upsell-step]');
    this.upsellProgress = this.querySelector('[data-embroidery-upsell-progress]');
    this.upsellStatus = this.querySelector('[data-embroidery-upsell-status]');
    this.personalizationStep = this.querySelector('[data-embroidery-personalization-step]');
    this.itemsContainer = this.querySelector('[data-embroidery-items]');
    this.preview = this.querySelector('.embroidery-preview');
    this.previewImage = this.querySelector('[data-embroidery-preview-image]');
    this.previewStatus = this.querySelector('[data-embroidery-preview-status]');
    this.letterInputs = Array.from(this.querySelectorAll('[data-embroidery-letter]'));
    this.status = this.querySelector('[data-embroidery-status]');
    this.applyButton = this.querySelector('[data-embroidery-apply]');
    this.applyLabel = this.querySelector('[data-embroidery-apply-label]');
    this.savingLabel = this.querySelector('[data-embroidery-saving-label]');
    this.eligibleProductIds = new Set(this.config.products.map((product) => String(product.id)));
    this.eligibleProductHandles = new Set(this.config.products.map((product) => product.handle));
    this.eligibleVariantIds = new Set(
      this.config.products.flatMap((product) => product.variants.map((variant) => String(variant.id)))
    );
    this.units = [];
    this.cartNote = '';
    this.selectedIndex = 0;
    this.checkoutTrigger = null;
    this.bypassCheckout = false;
    this.isPreparing = false;
    this.isAddingUpsell = false;
    this.cartChangedInDialog = false;
    this.cartAddTimer = null;
    this.aiPreviewTimer = null;
    this.aiPreviewSequence = 0;
    this.aiPreviewStorageKey = 'four-seasons-embroidery-previews-v5';
    this.aiPreviewCacheTtl = 25 * 60 * 1000;
    this.aiPreviewCache = this.loadStoredAiPreviews();
    this.aiPreviewRequests = new Map();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
    this.onCartAdded = this.onCartAdded.bind(this);
  }

  connectedCallback() {
    if (!this.config.enabled || this.eligibleProductIds.size === 0) return;
    this.installCartAddObserver();
    window.addEventListener('click', this.onDocumentClick, true);
    window.addEventListener('submit', this.onDocumentSubmit, true);
    window.addEventListener('embroidery:cart-added', this.onCartAdded);
    this.removeLegacyServiceLine();
    this.querySelector('[data-embroidery-close]').addEventListener('click', () => this.close());
    this.querySelector('[data-embroidery-upsell-dismiss]').addEventListener('click', () => this.close());
    this.querySelector('[data-embroidery-skip]').addEventListener('click', () => this.continueToCheckout());
    this.querySelector('[data-embroidery-form]').addEventListener('submit', (event) => this.apply(event));
    this.querySelector('[data-embroidery-upsell-products]').addEventListener('click', (event) => {
      const button = event.target.closest('[data-embroidery-upsell-add]');
      if (button) this.addUpsellProduct(button);
    });
    this.itemsContainer.addEventListener('click', (event) => {
      const button = event.target.closest('[data-unit-index]');
      if (button) this.selectUnit(Number(button.dataset.unitIndex));
    });
    this.letterInputs.forEach((input, index) => {
      input.addEventListener('input', () => this.onLetterInput(input, index));
      input.addEventListener('keydown', (event) => this.onLetterKeydown(event, index));
      input.addEventListener('paste', (event) => this.onLetterPaste(event, index));
    });
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.onDocumentClick, true);
    window.removeEventListener('submit', this.onDocumentSubmit, true);
    window.removeEventListener('embroidery:cart-added', this.onCartAdded);
    window.clearTimeout(this.cartAddTimer);
    window.clearTimeout(this.aiPreviewTimer);
  }

  installCartAddObserver() {
    if (window.__complimentaryEmbroideryFetchPatched) return;
    window.__complimentaryEmbroideryFetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    const cartAddPath = new URL(this.config.cartAddUrl, window.location.origin).pathname.replace(/\.js$/, '');

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const requestUrl = typeof args[0] === 'string' ? args[0] : args[0].url;
        const requestPath = new URL(requestUrl, window.location.origin).pathname.replace(/\.js$/, '');
        if (response.ok && requestPath === cartAddPath) {
          const data = await response.clone().json();
          window.dispatchEvent(new CustomEvent('embroidery:cart-added', { detail: data }));
        }
      } catch (error) {
        // Preview detection must never interfere with a successful cart request.
      }
      return response;
    };
  }

  onCartAdded(event) {
    if (this.isAddingUpsell || !this.addedPayloadIsEligible(event.detail)) return;
    window.clearTimeout(this.cartAddTimer);
    this.cartAddTimer = window.setTimeout(() => this.respondToQualifyingAdd(), 350);
  }

  addedPayloadIsEligible(payload) {
    const items = Array.isArray(payload && payload.items) ? payload.items : [payload];
    return items.some((item) => {
      if (!item) return false;
      return (
        this.eligibleProductIds.has(String(item.product_id || '')) ||
        this.eligibleVariantIds.has(String(item.variant_id || item.id || '')) ||
        this.eligibleProductHandles.has(item.handle || item.product_handle || '')
      );
    });
  }

  async respondToQualifyingAdd() {
    if (this.isPreparing || this.dialog.open) return;
    this.isPreparing = true;
    try {
      const cart = await this.getCart();
      const eligibleItems = this.getEligibleItems(cart);
      const eligibleQuantity = this.getEligibleQuantity(eligibleItems);
      if (eligibleQuantity >= this.config.minimumQuantity) {
        this.showPersonalization(cart, eligibleItems);
      } else if (eligibleQuantity > 0) {
        this.showUpsell(this.config.minimumQuantity - eligibleQuantity);
      }
    } catch (error) {
      // Adding to cart has already succeeded, so leave the normal cart experience intact.
    } finally {
      this.isPreparing = false;
    }
  }

  onDocumentClick(event) {
    if (this.bypassCheckout || this.dialog.open) return;
    const trigger = event.target.closest(
      'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], .bls-btn-checkout, [data-checkout]'
    );
    if (!trigger || trigger.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.checkoutTrigger = trigger;
    this.prepareCheckout();
  }

  onDocumentSubmit(event) {
    if (this.bypassCheckout || this.dialog.open) return;
    const submitter = event.submitter;
    if (!submitter || (submitter.name !== 'checkout' && !submitter.matches('.bls-btn-checkout, [data-checkout]')))
      return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.checkoutTrigger = submitter;
    this.prepareCheckout();
  }

  async prepareCheckout() {
    if (this.isPreparing) return;
    this.isPreparing = true;
    try {
      const cart = await this.getCart();
      const eligibleItems = this.getEligibleItems(cart);
      if (this.getEligibleQuantity(eligibleItems) < this.config.minimumQuantity) {
        this.resumeOriginalCheckout();
        return;
      }
      this.showPersonalization(cart, eligibleItems);
    } catch (error) {
      this.resumeOriginalCheckout();
    } finally {
      this.isPreparing = false;
    }
  }

  getCart() {
    return this.fetchJSON(this.config.cartJsonUrl, {
      headers: { Accept: 'application/json' },
    });
  }

  async removeLegacyServiceLine() {
    if (!this.config.serviceVariantId) return;
    try {
      const cart = await this.getCart();
      const serviceLines = cart.items.filter(
        (item) => Number(item.variant_id) === Number(this.config.serviceVariantId)
      );
      if (serviceLines.length === 0) return;
      const updates = Object.fromEntries(serviceLines.map((item) => [item.key, 0]));
      await this.fetchJSON(this.config.cartUpdateUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      window.location.reload();
    } catch (error) {
      return;
    }
  }

  getEligibleItems(cart) {
    return cart.items.filter((item) => {
      return (
        this.eligibleProductIds.has(String(item.product_id)) ||
        this.eligibleProductHandles.has(item.handle || item.product_handle || '')
      );
    });
  }

  getEligibleQuantity(items) {
    return items.reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  showUpsell(remainingQuantity) {
    this.dialogTitle.textContent = this.config.labels.upsellTitle;
    this.dialogDescription.textContent = this.config.labels.upsellDescription;
    this.upsellProgress.textContent =
      remainingQuantity === 1
        ? this.config.labels.upsellProgressOne
        : this.config.labels.upsellProgressMany.replace('__COUNT__', remainingQuantity);
    this.upsellStatus.textContent = '';
    this.upsellStep.hidden = false;
    this.personalizationStep.hidden = true;
    this.openDialog();
  }

  async addUpsellProduct(button) {
    const product = button.closest('[data-embroidery-upsell-product]');
    const variantSelect = product.querySelector('[data-embroidery-upsell-variant]');
    const variantId = variantSelect.value;
    if (!variantId || this.isAddingUpsell) return;

    this.isAddingUpsell = true;
    this.upsellStatus.textContent = '';
    this.setUpsellBusy(true, button);
    try {
      await this.fetchJSON(this.config.cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });
      this.cartChangedInDialog = true;
      const cart = await this.getCart();
      const eligibleItems = this.getEligibleItems(cart);
      const eligibleQuantity = this.getEligibleQuantity(eligibleItems);
      this.updateCartCounts(cart);
      if (eligibleQuantity >= this.config.minimumQuantity) {
        this.showPersonalization(cart, eligibleItems);
      } else {
        this.showUpsell(this.config.minimumQuantity - eligibleQuantity);
      }
    } catch (error) {
      this.upsellStatus.textContent = this.config.labels.upsellError;
    } finally {
      this.isAddingUpsell = false;
      this.setUpsellBusy(false, button);
    }
  }

  setUpsellBusy(isBusy, activeButton) {
    this.querySelectorAll('[data-embroidery-upsell-add], [data-embroidery-upsell-variant]').forEach((control) => {
      control.disabled = isBusy;
    });
    this.querySelector('[data-embroidery-upsell-dismiss]').disabled = isBusy;
    this.querySelector('[data-embroidery-close]').disabled = isBusy;
    if (activeButton) activeButton.textContent = isBusy ? this.config.labels.adding : this.config.labels.addRobe;
  }

  updateCartCounts(cart) {
    document.querySelectorAll('.cart-count').forEach((count) => {
      count.textContent = count.classList.contains('cart-count-drawer') ? `(${cart.item_count})` : cart.item_count;
      count.classList.toggle('cart-has-items', cart.item_count > 0);
    });
    const total = document.querySelector('header-total-price');
    if (total && typeof total.updateTotal === 'function') total.updateTotal(cart);
  }

  showPersonalization(cart, eligibleItems) {
    this.cartNote = cart.note || '';
    this.serviceLine = cart.items.find((item) => Number(item.variant_id) === Number(this.config.serviceVariantId));
    this.dialogTitle.textContent = this.config.labels.personalizationTitle;
    this.dialogDescription.textContent = this.config.labels.personalizationDescription;
    this.buildUnits(eligibleItems);
    this.renderItems();
    this.prefetchAiPreviews();
    this.selectUnit(0);
    this.upsellStep.hidden = true;
    this.personalizationStep.hidden = false;
    this.openDialog();
  }

  openDialog() {
    if (!this.dialog.open) this.dialog.showModal();
  }

  buildUnits(items) {
    this.units = [];
    items.forEach((item) => {
      const savedInitials = this.cleanInitials(item.properties && item.properties.Embroidery);
      for (let unitIndex = 0; unitIndex < item.quantity; unitIndex += 1) {
        this.units.push({
          lineKey: item.key,
          variantId: item.variant_id,
          productId: item.product_id,
          title: item.product_title,
          variantTitle: item.variant_title === 'Default Title' ? '' : item.variant_title,
          image: item.featured_image && item.featured_image.url ? item.featured_image.url : item.image,
          initials: savedInitials,
          properties: { ...(item.properties || {}) },
          sellingPlan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null,
          originalQuantity: item.quantity,
        });
      }
    });
  }

  renderItems() {
    this.itemsContainer.replaceChildren();
    this.units.forEach((unit, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'embroidery-item';
      button.dataset.unitIndex = index;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(index === this.selectedIndex));

      const image = document.createElement('img');
      image.src = unit.image || '';
      image.alt = '';
      image.width = 48;
      image.height = 48;

      const copy = document.createElement('span');
      const name = document.createElement('span');
      name.className = 'embroidery-item__name';
      name.textContent = `${this.config.labels.item} ${index + 1}: ${unit.title}`;
      copy.appendChild(name);
      if (unit.variantTitle) {
        const variant = document.createElement('span');
        variant.className = 'embroidery-item__variant';
        variant.textContent = unit.variantTitle;
        copy.appendChild(variant);
      }

      const value = document.createElement('span');
      value.className = 'embroidery-item__value';
      value.dataset.unitValue = index;
      value.textContent = unit.initials || '—';
      button.append(image, copy, value);
      this.itemsContainer.appendChild(button);
    });
  }

  selectUnit(index) {
    if (!this.units[index]) return;
    this.selectedIndex = index;
    this.itemsContainer.querySelectorAll('[data-unit-index]').forEach((button) => {
      button.setAttribute('aria-selected', String(Number(button.dataset.unitIndex) === index));
    });
    const unit = this.units[index];
    this.letterInputs.forEach((input, inputIndex) => {
      input.value = unit.initials.charAt(inputIndex);
    });
    this.updatePreview();
  }

  onLetterInput(input, index) {
    input.value = this.cleanInitials(input.value).charAt(0);
    this.updateSelectedInitials();
    if (input.value && this.letterInputs[index + 1]) this.letterInputs[index + 1].focus();
  }

  onLetterKeydown(event, index) {
    if (event.key === 'Backspace' && !event.currentTarget.value && this.letterInputs[index - 1]) {
      this.letterInputs[index - 1].focus();
    }
    if (event.key === 'ArrowLeft' && this.letterInputs[index - 1]) this.letterInputs[index - 1].focus();
    if (event.key === 'ArrowRight' && this.letterInputs[index + 1]) this.letterInputs[index + 1].focus();
  }

  onLetterPaste(event, startIndex) {
    const pasted = this.cleanInitials(event.clipboardData.getData('text'));
    if (!pasted) return;
    event.preventDefault();
    pasted.split('').forEach((letter, offset) => {
      if (this.letterInputs[startIndex + offset]) this.letterInputs[startIndex + offset].value = letter;
    });
    this.updateSelectedInitials();
    const focusIndex = Math.min(startIndex + pasted.length, this.letterInputs.length - 1);
    this.letterInputs[focusIndex].focus();
  }

  updateSelectedInitials() {
    const unit = this.units[this.selectedIndex];
    if (!unit) return;
    unit.initials = this.cleanInitials(this.letterInputs.map((input) => input.value).join(''));
    const itemValue = this.itemsContainer.querySelector(`[data-unit-value="${this.selectedIndex}"]`);
    if (itemValue) itemValue.textContent = unit.initials || '—';
    this.updatePreview();
  }

  updatePreview() {
    const unit = this.units[this.selectedIndex];
    const initials = unit ? unit.initials : '';
    const previewKey = unit ? `${unit.image || ''}\n${initials}` : '';

    window.clearTimeout(this.aiPreviewTimer);
    this.aiPreviewSequence += 1;
    this.preview.classList.remove('is-ai-rendered', 'is-generating');
    this.previewImage.removeAttribute('crossorigin');
    this.previewImage.src = unit ? unit.image || '' : '';
    this.previewImage.alt = unit ? unit.title : '';
    this.previewStatus.textContent = '';

    if (!unit || !initials || !this.config.aiPreviewEnabled || !this.config.aiPreviewUrl || !unit.image) {
      return;
    }

    const cachedImage = this.aiPreviewCache.get(previewKey);
    if (cachedImage) {
      const selectedIndex = this.selectedIndex;
      const sequence = this.aiPreviewSequence;
      this.showAiPreview(cachedImage, selectedIndex, previewKey, sequence).catch(() => {
        if (this.aiPreviewCache.get(previewKey) === cachedImage) {
          this.deleteStoredAiPreview(previewKey);
        }
        if (this.isPreviewCurrent(selectedIndex, previewKey, sequence)) {
          this.requestAiPreview(unit, selectedIndex, previewKey, sequence);
        }
      });
      return;
    }

    const selectedIndex = this.selectedIndex;
    const sequence = this.aiPreviewSequence;
    const pendingImage = this.aiPreviewRequests.get(previewKey);
    if (pendingImage) {
      this.preview.classList.add('is-generating');
      this.previewStatus.textContent = this.config.labels.previewGenerating;
      pendingImage
        .then((imageUrl) => this.showAiPreview(imageUrl, selectedIndex, previewKey, sequence))
        .catch(() => {
          this.deleteStoredAiPreview(previewKey);
          if (this.isPreviewCurrent(selectedIndex, previewKey, sequence)) {
            this.preview.classList.remove('is-generating', 'is-ai-rendered');
            this.previewStatus.textContent = this.config.labels.previewFallback;
          }
        });
      return;
    }

    this.aiPreviewTimer = window.setTimeout(() => {
      this.requestAiPreview(unit, selectedIndex, previewKey, sequence);
    }, 1400);
  }

  prefetchAiPreviews() {
    if (!this.config.aiPreviewEnabled || !this.config.aiPreviewUrl) return;
    this.units.forEach((unit) => {
      if (!unit.initials || !unit.image) return;
      const previewKey = `${unit.image}\n${unit.initials}`;
      this.loadAiPreview(unit, previewKey).catch(() => {});
    });
  }

  async requestAiPreview(unit, selectedIndex, previewKey, sequence) {
    if (!this.isPreviewCurrent(selectedIndex, previewKey, sequence)) return;
    this.preview.classList.add('is-generating');
    this.previewStatus.textContent = this.config.labels.previewGenerating;

    try {
      const imageUrl = await this.loadAiPreview(unit, previewKey);
      await this.showAiPreview(imageUrl, selectedIndex, previewKey, sequence);
    } catch (error) {
      if (this.isPreviewCurrent(selectedIndex, previewKey, sequence)) {
        this.preview.classList.remove('is-generating', 'is-ai-rendered');
        this.previewStatus.textContent = this.config.labels.previewFallback;
      }
    }
  }

  loadAiPreview(unit, previewKey) {
    const cachedImage = this.aiPreviewCache.get(previewKey);
    if (cachedImage) return Promise.resolve(cachedImage);

    const pendingImage = this.aiPreviewRequests.get(previewKey);
    if (pendingImage) return pendingImage;

    const request = this.fetchAiPreview(unit)
      .then((imageUrl) => {
        this.storeAiPreview(previewKey, imageUrl);
        return imageUrl;
      })
      .finally(() => this.aiPreviewRequests.delete(previewKey));
    this.aiPreviewRequests.set(previewKey, request);
    return request;
  }

  loadStoredAiPreviews() {
    const previews = new Map();
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.aiPreviewStorageKey) || '[]');
      const now = Date.now();
      stored.forEach(([key, entry]) => {
        if (entry && entry.imageUrl && entry.expiresAt > now) previews.set(key, entry.imageUrl);
      });
    } catch (error) {
      return previews;
    }
    return previews;
  }

  storeAiPreview(previewKey, imageUrl) {
    this.aiPreviewCache.set(previewKey, imageUrl);
    try {
      const expiresAt = Date.now() + this.aiPreviewCacheTtl;
      const stored = JSON.parse(window.localStorage.getItem(this.aiPreviewStorageKey) || '[]');
      const entries = stored.filter(([key, entry]) => key !== previewKey && entry && entry.expiresAt > Date.now());
      entries.push([previewKey, { imageUrl, expiresAt }]);
      window.localStorage.setItem(this.aiPreviewStorageKey, JSON.stringify(entries.slice(-12)));
    } catch (error) {
      return;
    }
  }

  deleteStoredAiPreview(previewKey) {
    this.aiPreviewCache.delete(previewKey);
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.aiPreviewStorageKey) || '[]');
      window.localStorage.setItem(
        this.aiPreviewStorageKey,
        JSON.stringify(stored.filter(([key]) => key !== previewKey))
      );
    } catch (error) {
      return;
    }
  }

  async fetchAiPreview(unit) {
    const response = await fetch(this.config.aiPreviewUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      body: JSON.stringify({ imageUrl: unit.image, initials: unit.initials }),
    });
    const payload = await response.json();
    if (!response.ok && response.status !== 202) {
      throw new Error(payload.error || 'AI preview request failed');
    }

    const readyPayload = payload.status === 'ready' ? payload : await this.pollAiPreview(payload.requestId);
    if (readyPayload.status !== 'ready') throw new Error(readyPayload.error || 'AI preview generation failed');
    return this.getAiPreviewImageUrl(readyPayload.requestId);
  }

  async pollAiPreview(requestId) {
    if (!requestId) throw new Error('Missing preview request ID');

    for (let attempt = 0; attempt < 40; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      const response = await fetch(this.getAiPreviewStatusUrl(requestId), {
        headers: { Accept: 'application/json' },
      });
      const payload = await response.json();
      if (payload.status === 'ready') return payload;
      if (payload.status === 'failed' || (!response.ok && response.status !== 202)) {
        throw new Error(payload.error || 'AI preview generation failed');
      }
    }

    throw new Error('AI preview generation timed out');
  }

  getAiPreviewStatusUrl(requestId) {
    const url = new URL(this.config.aiPreviewUrl, window.location.origin);
    url.searchParams.set('requestId', requestId);
    return url.toString();
  }

  getAiPreviewImageUrl(requestId) {
    const url = new URL(this.getAiPreviewStatusUrl(requestId));
    url.searchParams.set('image', '1');
    return url.toString();
  }

  showAiPreview(imageUrl, selectedIndex, previewKey, sequence) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      // Send Origin even when the shopper's browser suppresses Referer.
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        if (this.isPreviewCurrent(selectedIndex, previewKey, sequence)) {
          this.previewImage.crossOrigin = 'anonymous';
          this.previewImage.src = imageUrl;
          this.preview.classList.remove('is-generating');
          this.preview.classList.add('is-ai-rendered');
          this.previewStatus.textContent = this.config.labels.previewReady;
        }
        resolve();
      };
      image.onerror = reject;
      image.src = imageUrl;
    });
  }

  isPreviewCurrent(selectedIndex, previewKey, sequence) {
    const unit = this.units[selectedIndex];
    return (
      this.selectedIndex === selectedIndex &&
      this.aiPreviewSequence === sequence &&
      Boolean(unit) &&
      `${unit.image || ''}\n${unit.initials}` === previewKey
    );
  }

  cleanInitials(value) {
    return String(value || '')
      .replace(/[^a-z]/gi, '')
      .slice(0, 3)
      .toUpperCase();
  }

  async apply(event) {
    event.preventDefault();
    this.setBusy(true);
    this.status.textContent = '';
    const originalLines = this.getOriginalLines();
    const updates = Object.fromEntries(originalLines.map((line) => [line.key, 0]));
    if (this.serviceLine) updates[this.serviceLine.key] = 0;
    const orderNote = this.buildOrderNote();
    let originalsRemoved = false;
    try {
      await this.fetchJSON(this.config.cartUpdateUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates, note: orderNote }),
      });
      originalsRemoved = true;
      await this.addPersonalizedItems();
      this.continueToCheckout();
    } catch (error) {
      if (originalsRemoved) {
        try {
          await this.restoreOriginalItems(originalLines);
          await this.restoreCartNote();
        } catch (restoreError) {
          window.location.reload();
          return;
        }
      }
      this.status.textContent = this.config.labels.error;
      this.setBusy(false);
    }
  }

  buildOrderNote() {
    const heading = 'COMPLIMENTARY EMBROIDERY';
    const existingNote = String(this.cartNote || '')
      .replace(new RegExp(`(?:^|\\n{2})${heading}\\n[\\s\\S]*$`), '')
      .trim();
    const personalizedUnits = this.units.filter((unit) => unit.initials);
    if (personalizedUnits.length === 0) return existingNote;
    const details = personalizedUnits.map((unit, index) => {
      const variant = unit.variantTitle ? ` — ${unit.variantTitle}` : '';
      return `${index + 1}. ${unit.title}${variant}: ${unit.initials}`;
    });
    return [existingNote, `${heading}\n${details.join('\n')}`].filter(Boolean).join('\n\n');
  }

  getOriginalLines() {
    const lines = new Map();
    this.units.forEach((unit) => {
      if (!lines.has(unit.lineKey)) {
        lines.set(unit.lineKey, {
          key: unit.lineKey,
          id: unit.variantId,
          quantity: unit.originalQuantity,
          properties: unit.properties,
          selling_plan: unit.sellingPlan,
        });
      }
    });
    return Array.from(lines.values());
  }

  async addPersonalizedItems() {
    const items = this.units.map((unit) => {
      const properties = { ...unit.properties };
      delete properties.Embroidery;
      delete properties._complimentary_embroidery;
      if (unit.initials) {
        properties.Embroidery = unit.initials;
        properties._complimentary_embroidery = 'Yes';
      }
      const item = { id: unit.variantId, quantity: 1, properties };
      if (unit.sellingPlan) item.selling_plan = unit.sellingPlan;
      return item;
    });
    return this.fetchJSON(this.config.cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
  }

  restoreOriginalItems(lines) {
    const items = lines.map((line) => {
      const item = {
        id: line.id,
        quantity: line.quantity,
        properties: line.properties,
      };
      if (line.selling_plan) item.selling_plan = line.selling_plan;
      return item;
    });
    return this.fetchJSON(this.config.cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
  }

  restoreCartNote() {
    return this.fetchJSON(this.config.cartUpdateUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ note: this.cartNote }),
    });
  }

  async fetchJSON(url, options = {}) {
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  }

  setBusy(isBusy) {
    this.applyButton.disabled = isBusy;
    this.querySelector('[data-embroidery-skip]').disabled = isBusy;
    this.querySelector('[data-embroidery-close]').disabled = isBusy;
    this.letterInputs.forEach((input) => {
      input.disabled = isBusy;
    });
    this.applyLabel.hidden = isBusy;
    this.savingLabel.hidden = !isBusy;
  }

  close() {
    if (this.applyButton.disabled || this.isAddingUpsell) return;
    this.dialog.close();
    if (this.cartChangedInDialog) window.location.reload();
  }

  continueToCheckout() {
    this.bypassCheckout = true;
    this.dialog.close();
    window.location.assign(this.config.checkoutUrl);
  }

  resumeOriginalCheckout() {
    const trigger = this.checkoutTrigger;
    this.bypassCheckout = true;
    if (trigger && trigger.tagName === 'A') {
      window.location.assign(trigger.href);
      return;
    }
    const form = trigger && trigger.form;
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(trigger);
      return;
    }
    window.location.assign(this.config.checkoutUrl);
  }
}

if (!customElements.get('complimentary-embroidery')) {
  customElements.define('complimentary-embroidery', ComplimentaryEmbroidery);
}
